'use client';

import * as React from 'react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { Button, Box, Flex } from '@chakra-ui/react';
import { createSafeContext } from '@/utils/react';
import LucideIcon from '../LucideIcon';
import type { ComponentProps } from 'react';

export type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

export type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
} & React.ComponentProps<typeof Box>;

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
} & CarouselProps;

export const [CarouselProvider, useCarousel] = createSafeContext<CarouselContextProps>('CarouselContext');

type CarouselCompoundComponent = React.ForwardRefExoticComponent<
  CarouselProps & React.RefAttributes<HTMLDivElement>
> & {
  Content: typeof CarouselContent;
  Item: typeof CarouselItem;
  Previous: typeof CarouselPrevious;
  Next: typeof CarouselNext;
  DotButton: typeof CarouselDotButton;
  Dots: typeof CarouselDots;
};

const CarouselContent = React.forwardRef<HTMLDivElement, ComponentProps<typeof Flex>>(
  ({ className, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();

    return (
      <Box ref={carouselRef} overflow="hidden">
        <Flex
          ref={ref}
          className={className}
          flexDirection={orientation === 'horizontal' ? 'row' : 'column'}
          {...props}
        />
      </Box>
    );
  },
);

CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<HTMLDivElement, ComponentProps<typeof Box>>(({ className, ...props }, ref) => {
  return (
    <Box
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={className}
      minW="0"
      flexShrink="0"
      flexGrow="0"
      flexBasis="full"
      {...props}
    />
  );
});

CarouselItem.displayName = 'CarouselItem';

export type CarouselDotButtonProps = {
  index: number;
  children?: React.ReactNode;
  render?: (props: { onClick: () => void; isSelected: boolean; index: number }) => React.ReactNode;
} & Omit<React.ComponentProps<typeof Button>, 'variant'>;

const CarouselDotButton = React.forwardRef<HTMLButtonElement, CarouselDotButtonProps>(
  ({ className, index, children, render, ...props }, ref) => {
    const { selectedIndex, onDotButtonClick } = useCarousel();
    const isSelected = index === selectedIndex;
    const onClick = React.useCallback(() => {
      onDotButtonClick(index);
    }, [onDotButtonClick, index]);

    if (render) {
      return <>{render({ onClick, isSelected, index })}</>;
    }

    return (
      <Button
        ref={ref}
        onClick={onClick}
        size="xs"
        aria-current={isSelected}
        variant="ghost"
        data-selected={isSelected}
        className={className}
        {...props}
      >
        {children}
      </Button>
    );
  },
);

CarouselDotButton.displayName = 'CarouselDotButton';

export type CarouselDotsProps = {
  children?: React.ReactNode;
  renderDot?: (props: { onClick: () => void; isSelected: boolean; index: number }) => React.ReactNode;
} & ComponentProps<typeof Flex>;

const CarouselDots = React.forwardRef<HTMLDivElement, CarouselDotsProps>(({ renderDot, className, ...props }, ref) => {
  const { scrollSnaps } = useCarousel();

  return (
    <Flex ref={ref} className={className} {...props}>
      {scrollSnaps.map((_, index) => (
        <CarouselDotButton
          key={index}
          index={index}
          render={renderDot ? (buttonProps) => renderDot(buttonProps) : undefined}
        />
      ))}
    </Flex>
  );
});

CarouselDots.displayName = 'CarouselDots';

const CarouselPrevious = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ className, variant = 'outline', size = 'sm', ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        position="absolute"
        height="8"
        width="8"
        borderRadius="full"
        left={orientation === 'horizontal' ? '-3rem' : '50%'}
        top={orientation === 'horizontal' ? '50%' : '-3rem'}
        transform={orientation === 'horizontal' ? 'translateY(-50%)' : 'translateX(-50%) rotate(90deg)'}
        className={className}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        {...props}
      >
        <LucideIcon name="ChevronLeft" boxSize={16} />

        <span className="sr-only">Previous</span>
      </Button>
    );
  },
);

CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ className, variant = 'outline', size = 'sm', ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        position="absolute"
        height="8"
        width="8"
        borderRadius="full"
        right={orientation === 'horizontal' ? '-3rem' : undefined}
        bottom={orientation === 'vertical' ? '-3rem' : undefined}
        left={orientation === 'vertical' ? '50%' : undefined}
        top={orientation === 'horizontal' ? '50%' : undefined}
        transform={orientation === 'horizontal' ? 'translateY(-50%)' : 'translateX(-50%) rotate(90deg)'}
        className={className}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        <LucideIcon name="ChevronRight" boxSize={16} />
        <span className="sr-only">Next</span>
      </Button>
    );
  },
);

CarouselNext.displayName = 'CarouselNext';

const CarouselComponent = React.forwardRef<HTMLDivElement, CarouselProps>(
  ({ orientation = 'horizontal', opts, setApi, plugins, className, children, ...props }, ref) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);

    const onDotButtonClick = React.useCallback(
      (index: number) => {
        if (!api) {
          return;
        }
        api.scrollTo(index);
      },
      [api],
    );

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setSelectedIndex(api.selectedScrollSnap());
      setScrollSnaps(api.scrollSnapList());
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);

      return () => {
        api?.off('select', onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselProvider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
          selectedIndex,
          scrollSnaps,
          onDotButtonClick,
        }}
      >
        <Box
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={className}
          position="relative"
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </Box>
      </CarouselProvider>
    );
  },
);

CarouselComponent.displayName = 'Carousel';

export const Carousel = Object.assign(CarouselComponent, {
  Content: CarouselContent,
  Item: CarouselItem,
  Previous: CarouselPrevious,
  Next: CarouselNext,
  DotButton: CarouselDotButton,
  Dots: CarouselDots,
}) as CarouselCompoundComponent;
